import { rehydrateSession, resetRehydration, useSession } from "@/features/auth";
import { TOKEN_STORAGE_KEY, adoptStoredToken } from "@/shared/api-fetch";
import { router } from "./router";

/**
 * Reconciles this tab when the token changes in another one.
 *
 * `localStorage` is shared across every tab on the origin — the persistence this feature adds is
 * exactly what makes that a new risk. Without this, signing out in tab B leaves tab A still
 * rendering a live session against a token B just deleted; tab A only finds out when its next
 * request 401s. A stale tab silently 401ing on its next action is a worse outcome than an explicit
 * one, so this reconciles eagerly the moment the browser reports the change, rather than waiting
 * for that to happen.
 *
 * **What "reconcile" means here:** treat any change to the stored token as "the truth about who is
 * signed in on this origin may have just changed," and re-derive it the same way a fresh page load
 * would — never assume the new value is trustworthy on its own. `adoptStoredToken` (not
 * `setToken`) mirrors the value into THIS tab's in-memory token without writing it back to
 * storage — the tab that fired this event already wrote it, and writing the same key again risks
 * racing whatever that tab, or a third one, writes next. `useSession().clear()` drops whatever
 * identity this tab had adopted, because it was resolved against the token that just changed and
 * must not keep being shown as current. `resetRehydration()` is required because the page-load
 * one-shot has already resolved by the time a LATER `storage` event arrives — without it this
 * would silently no-op forever after the first reload. `rehydrateSession()` then does the actual
 * work: adopts the new token's identity if `/v1/me` agrees, clears a token `/v1/me` rejects
 * outright, and leaves an unreachable-backend token alone for a later retry — the same three-way
 * split a reload gets, so a same-person login in another tab resolves quietly here while an
 * outright sign-out sends this tab to `/login` rather than leaving it showing stale, unusable
 * content.
 *
 * `key` is `null` when another tab calls `localStorage.clear()` outright rather than removing this
 * key specifically — treated the same as our own key changing, since a full clear takes the token
 * with it (and the spec guarantees `newValue` is `null` alongside it).
 */
export function reconcileTokenAcrossTabs(
  event: Pick<StorageEvent, "key" | "newValue">,
): Promise<void> {
  if (event.key !== null && event.key !== TOKEN_STORAGE_KEY) return Promise.resolve();

  adoptStoredToken(event.newValue);
  useSession().clear();
  resetRehydration();

  return rehydrateSession().then(() => {
    if (!useSession().isAuthed) void router.push("/login");
  });
}

/** Wires `reconcileTokenAcrossTabs` to the real browser event. Call once, at startup. */
export function watchTokenAcrossTabs(): void {
  window.addEventListener("storage", (event) => {
    void reconcileTokenAcrossTabs(event);
  });
}
